"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, AlertCircle } from "lucide-react";
import { Customer, Vehicle, Subscription } from "@/types";
import { Subscription as SubscriptionPlanType } from "@/types/subscription.types";
import { PaymentMode } from "@/types/enums.types";
import { SubscriptionFeature } from "@/types/userSubscription.types";
import { formatCurrency, generateTimeSlots } from "@/lib/utils";

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Generate time slots with 15-minute intervals
const timeSlots = generateTimeSlots();

export interface AddSubscriptionFormData {
  customerId: string;
  subscriptionId: string;
  vehicleId?: string;
  recurringDay: string;
  preferredTime: string;
  paymentMode: PaymentMode;
  discount?: number;
}

interface AddSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: Customer[];
  vehicles: Vehicle[];
  subscriptionPlans: SubscriptionPlanType[];
  subscriptions: CustomerSubscriptionType[];
  onCreate: (
    data: AddSubscriptionFormData,
    planFeatures?: SubscriptionFeature[]
  ) => Promise<void>;
}

export function AddSubscriptionDialog({
  open,
  onOpenChange,
  customers,
  vehicles,
  subscriptionPlans,
  subscriptions,
  onCreate,
}: AddSubscriptionDialogProps) {
  const [formData, setFormData] = useState<AddSubscriptionFormData>({
    customerId: "",
    subscriptionId: "",
    vehicleId: "",
    recurringDay: "Monday",
    preferredTime: "10:00 AM",
    paymentMode: "CASH",
    discount: 0,
  });

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers;
    const search = customerSearchTerm.toLowerCase();
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(search) ||
        c.phone.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search)
    );
  }, [customers, customerSearchTerm]);

  // Get vehicles for selected customer
  const customerVehicles = useMemo(() => {
    if (!formData.customerId) return [];
    return vehicles.filter((v) => v.customerId === formData.customerId);
  }, [vehicles, formData.customerId]);

  // Get selected subscription plan
  const selectedPlan = useMemo(() => {
    return subscriptionPlans.find((p) => p.id === formData.subscriptionId);
  }, [subscriptionPlans, formData.subscriptionId]);

  // Check if customer has active subscription
  useEffect(() => {
    if (formData.customerId) {
      const activeSub = subscriptions.find(
        (s) => s.customerId === formData.customerId && s.status === "active"
      );
      setHasActiveSubscription(!!activeSub);
    } else {
      setHasActiveSubscription(false);
    }
  }, [formData.customerId, subscriptions]);

  // Auto-select vehicle if customer has only one
  useEffect(() => {
    if (customerVehicles.length === 1 && !formData.vehicleId) {
      setFormData((prev) => ({
        ...prev,
        vehicleId: customerVehicles[0].id,
      }));
    } else if (customerVehicles.length === 0) {
      setFormData((prev) => ({
        ...prev,
        vehicleId: "",
      }));
    }
  }, [customerVehicles, formData.vehicleId]);

  const updateField = <K extends keyof AddSubscriptionFormData>(
    field: K,
    value: AddSubscriptionFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!formData.customerId) {
      return;
    }

    if (!formData.subscriptionId) {
      return;
    }

    if (hasActiveSubscription) {
      return;
    }

    // Verify we have the correct subscription ID (not name)
    if (!selectedPlan) {
      console.error("Selected plan not found for ID:", formData.subscriptionId);
      return;
    }

    // Ensure we're passing the ID, not the name
    const formDataWithId = {
      ...formData,
      subscriptionId: selectedPlan.id, // Explicitly use the plan's ID
    };

    // Pass the selected plan's features
    const selectedPlanFeatures = selectedPlan?.features || [];
    await onCreate(formDataWithId, selectedPlanFeatures);
    // Reset form
    setFormData({
      customerId: "",
      subscriptionId: "",
      vehicleId: "",
      recurringDay: "Monday",
      preferredTime: "10:00 AM",
      paymentMode: "CASH",
      discount: 0,
    });
    setCustomerSearchTerm("");
    setHasActiveSubscription(false);
  };

  const handleCancel = () => {
    setFormData({
      customerId: "",
      subscriptionId: "",
      vehicleId: "",
      recurringDay: "Monday",
      preferredTime: "10:00 AM",
      paymentMode: "CASH",
      discount: 0,
    });
    setCustomerSearchTerm("");
    setHasActiveSubscription(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Subscription for Customer</DialogTitle>
          <DialogDescription>
            Assign a subscription plan to a customer. Fields marked with * are
            required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Customer Selection */}
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer *</Label>
            <Select
              value={formData.customerId}
              onValueChange={(value) => updateField("customerId", value)}
            >
              <SelectTrigger id="customer">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                <div
                  className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or phone..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No customers found
                  </div>
                )}
              </SelectContent>
            </Select>
            {hasActiveSubscription && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This customer already has an active subscription. Please
                  cancel or pause the existing subscription before adding a new
                  one.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Subscription Plan Selection */}
          <div className="grid gap-2">
            <Label htmlFor="subscriptionPlan">Subscription Plan *</Label>
            <Select
              value={formData.subscriptionId}
              onValueChange={(value) => {
                updateField("subscriptionId", value);
              }}
            >
              <SelectTrigger id="subscriptionPlan">
                <SelectValue placeholder="Select subscription plan" />
              </SelectTrigger>
              <SelectContent>
                {subscriptionPlans
                  .filter((p) => !p.isDeleted)
                  .map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - {formatCurrency(plan.price)} /{" "}
                      {plan.duration}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {selectedPlan && (
              <div className="text-sm text-muted-foreground mt-1">
                Price: {formatCurrency(selectedPlan.price)} | Duration:{" "}
                {selectedPlan.duration}
              </div>
            )}
          </div>

          {/* Vehicle Selection */}
          <div className="grid gap-2">
            <Label htmlFor="vehicle">Vehicle (Optional)</Label>
            <Select
              value={formData.vehicleId || ""}
              onValueChange={(value) =>
                updateField("vehicleId", value || undefined)
              }
              disabled={!formData.customerId || customerVehicles.length === 0}
            >
              <SelectTrigger id="vehicle">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                {customerVehicles.length > 0 ? (
                  customerVehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    {formData.customerId
                      ? "No vehicles found for this customer"
                      : "Select a customer first"}
                  </div>
                )}
              </SelectContent>
            </Select>
            {formData.customerId && customerVehicles.length === 0 && (
              <p className="text-xs text-muted-foreground">
                This customer has no vehicles. You can add a vehicle later.
              </p>
            )}
          </div>

          {/* Schedule Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="recurringDay">Recurring Day (Optional)</Label>
              <Select
                value={formData.recurringDay}
                onValueChange={(value) => updateField("recurringDay", value)}
              >
                <SelectTrigger id="recurringDay">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {weekDays.map((day) => (
                    <SelectItem key={day} value={day}>
                      {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="preferredTime">Preferred Time (Optional)</Label>
              <Select
                value={formData.preferredTime}
                onValueChange={(value) => updateField("preferredTime", value)}
              >
                <SelectTrigger id="preferredTime">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.label}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payment Mode */}
          <div className="grid gap-2">
            <Label htmlFor="paymentMode">Payment Mode (Optional)</Label>
            <Select
              value={formData.paymentMode}
              onValueChange={(value) =>
                updateField("paymentMode", value as PaymentMode)
              }
            >
              <SelectTrigger id="paymentMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">Cash</SelectItem>
                <SelectItem value="CARD">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Discount */}
          <div className="grid gap-2">
            <Label htmlFor="discount">Discount (Optional)</Label>
            <Input
              id="discount"
              type="number"
              min={0}
              value={formData.discount || 0}
              onChange={(e) => updateField("discount", Number(e.target.value) || 0)}
              placeholder="Enter discount amount"
            />
            {selectedPlan && formData.discount && formData.discount > 0 && (
              <p className="text-sm text-muted-foreground">
                Final amount: {formatCurrency(selectedPlan.price - formData.discount)}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={
              !formData.customerId ||
              !formData.subscriptionId ||
              hasActiveSubscription
            }
          >
            Create Subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
